import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import { Box, Paper, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MetaChip } from "../../../components/UIToolkit/Badges/MetaChip";
import { SystemValidationTextField } from "../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { InlineHint } from "../../../components/UIToolkit/molecules/InlineHint";
import { SystemBanner } from "../../../components/UIToolkit/molecules/SystemBanner";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { earliestDateInBlob, SHIFTS_DATE_COLUMN } from "../loadOrder";
import { LoadOrderCaption } from "./LoadOrderCaption";
import { type LoadRecurringResult, LoadResultDisplay, type LoadShiftsResult } from "./LoadResultDisplay";
import type { LoadNursesResults } from "./NursesSummary";
import { PreviewCard } from "./PreviewCard";
import { RosterLoadResults } from "./RosterLoadResults";
import { KIND_LABEL_KEY, previewFileType, type useAutoPromptSection } from "./useAutoPromptSection";

type Props = { state: ReturnType<typeof useAutoPromptSection> };

export function AutoPromptTransformPanel({ state }: Props) {
	const { t } = useTranslation();
	const unresolvedFailures = state.failedTransformFiles.filter((file) => !state.transformFailures[file.id]?.skip);

	if (state.phase === "prompt") {
		return (
			<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }} data-testid="auto-prompt-final-prompt">
				<InlineHint tone="info" dataTestId="auto-prompt-manual-edits-hint">
					{t("onboarding_auto_prompt.manual_edits_hint")}
				</InlineHint>
				<SystemValidationTextField
					label={t("onboarding_auto_prompt.final_prompt")}
					value={state.finalPrompt}
					onChange={(event) => state.setFinalPrompt(event.target.value)}
					fullWidth
					injectedProps={{ multiline: true, minRows: 12 }}
					dataTestId="auto-prompt-final-prompt-field"
				/>
				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "flex-end", alignItems: "center" }}>
					<SystemButton
						buttonVariant="borderless"
						leadingIcon={<ContentCopyOutlined />}
						onClick={() => void state.handleCopyPrompt()}
						data-testid="auto-prompt-copy-prompt-button"
					>
						{t("onboarding_auto_prompt.copy_prompt")}
					</SystemButton>
					<SystemButton buttonVariant="secondary" onClick={state.handleBackToEditing} data-testid="auto-prompt-back-to-editing-button">
						{t("onboarding_auto_prompt.back_to_editing")}
					</SystemButton>
					<SystemButton
						onClick={() => void state.handleTransformAll()}
						disabled={!state.finalPrompt.trim() || state.isTransforming}
						loading={state.isTransforming}
						data-testid="auto-prompt-transform-button"
					>
						{t("onboarding_auto_prompt.transform")}
					</SystemButton>
				</Box>
			</Box>
		);
	}

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }} data-testid="auto-prompt-transform-results">
			<Paper variant="outlined" sx={{ p: 2 }} data-testid="auto-prompt-retransform">
				<Typography variant="overline" sx={{ display: "block", mb: 1, color: "text.secondary" }}>
					{t("onboarding_auto_prompt.transform_prompt")}
				</Typography>
				<SystemValidationTextField
					fullWidth
					value={state.finalPrompt}
					onChange={(event) => state.setFinalPrompt(event.target.value)}
					injectedProps={{ multiline: true, minRows: 8 }}
					dataTestId="auto-prompt-retransform-prompt"
				/>
				<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
					<SystemButton
						onClick={() => void state.handleTransformAll()}
						disabled={!state.finalPrompt.trim() || state.isTransforming}
						loading={state.isTransforming}
						data-testid="auto-prompt-retransform-button"
					>
						{t("onboarding_auto_prompt.transform_again")}
					</SystemButton>
				</Box>
			</Paper>

			{state.isTransforming && (
				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }} data-testid="auto-prompt-transforming-files">
					{state.files.map((file) => (
						<MetaChip key={file.id} label={t("onboarding_auto_prompt.preparing_file", { type: t(KIND_LABEL_KEY[file.kind]) })} tone="info" />
					))}
				</Box>
			)}

			{state.orderedFiles.map((file, index) => {
				const outcome = state.transformed[file.id];
				if (!outcome) return null;
				const previewType = previewFileType(file.kind);
				const status = outcome.uploaded ? t("onboarding_load.status.uploaded") : outcome.skip ? t("onboarding_load.status.skipped") : "";
				// The loader is selected from this same file kind, so the stored union member matches the result component below.
				const rosterResult = outcome.loadResult as LoadNursesResults;
				const scheduleResult = outcome.loadResult as LoadShiftsResult | LoadRecurringResult;
				return (
					<Box key={file.id}>
						<Typography variant="subtitle2" sx={{ mb: 0.5, color: "text.secondary" }}>
							{t("onboarding_auto_prompt.result_heading", { index: index + 1, type: t(KIND_LABEL_KEY[file.kind]), status })}
						</Typography>
						<PreviewCard
							fileName={file.file.name}
							fileType={previewType}
							transformedData={outcome.csvBuffer}
							dataTestIdPrefix={`auto-prompt-preview-${previewType}-${file.id}`}
							failedTabs={outcome.failedTabs}
							skip={outcome.skip}
							onSkipChange={(skip) => state.updateOutcome(file.id, { skip })}
							onCancel={() => state.handleCancelTransform(file.id)}
							onFilteredBlobChange={(blob) => {
								state.updateOutcome(file.id, { filteredBlob: blob });
								if (file.kind === "shifts") {
									void earliestDateInBlob(blob, SHIFTS_DATE_COLUMN).then((date) => state.updateOutcome(file.id, { earliestDate: date }));
								}
							}}
						/>
						{outcome.uploaded && outcome.loadResult && (
							<Box sx={{ mt: 2 }}>
								{file.kind === "roster" ? (
									<RosterLoadResults results={rosterResult} timezone={state.chosenDepartment?.timezone} dataTestId={`auto-prompt-result-${file.id}`} />
								) : (
									<LoadResultDisplay result={scheduleResult} departmentId={state.departmentId} timezone={state.chosenDepartment?.timezone} />
								)}
							</Box>
						)}
					</Box>
				);
			})}

			{state.failedTransformFiles.map((file) => {
				const failure = state.transformFailures[file.id];
				if (!failure) return null;
				return (
					<SystemBanner
						key={file.id}
						tone={failure.skip ? "warning" : "error"}
						title={file.file.name}
						description={failure.skip ? t("onboarding_auto_prompt.failed_file_skipped") : t(failure.errorKey)}
						action={
							<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
								<SystemButton
									size="compact"
									buttonVariant="secondary"
									loading={state.retryingTransformItemId === failure.itemId}
									onClick={() => void state.handleRetryTransform(file.id)}
									data-testid={`auto-prompt-retry-file-${file.id}`}
								>
									{t("onboarding_jobs.retry_file")}
								</SystemButton>
								{!failure.skip && (
									<SystemButton
										size="compact"
										buttonVariant="hairline"
										onClick={() => state.handleSkipFailedTransform(file.id)}
										data-testid={`auto-prompt-skip-file-${file.id}`}
									>
										{t("onboarding_load.skip_file")}
									</SystemButton>
								)}
								<SystemButton
									size="compact"
									buttonVariant="borderless"
									onClick={() => state.handleCancelTransform(file.id)}
									data-testid={`auto-prompt-remove-failed-file-${file.id}`}
								>
									{t("onboarding_load.remove_file")}
								</SystemButton>
							</Box>
						}
						dataTestId={`auto-prompt-transform-failure-${file.id}`}
					/>
				);
			})}

			<LoadOrderCaption entries={state.loadOrderEntries} dataTestId="auto-prompt-load-order" />
			<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
				<SystemButton
					onClick={() => void state.handleUploadAll()}
					disabled={state.isUploadAllBusy || state.isTransforming || state.allRemainingUploaded || unresolvedFailures.length > 0}
					loading={state.isUploadAllBusy}
					data-testid="auto-prompt-upload-all-button"
				>
					{state.allRemainingUploaded ? t("onboarding_load.all_uploaded") : t("onboarding_auto_prompt.upload_all")}
				</SystemButton>
			</Box>
		</Box>
	);
}
